import { useGetIdentity, useTable, useUpdate } from "@refinedev/core";

import { Badge } from "@/components/Badge";
import { SmallButton } from "@/components/Form";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Table, Td, Th } from "@/components/Table";
import { phaseLabel, plural, sensitivityLabel } from "@/lib/format";
import { filterValue } from "@/lib/query";
import type { AdminIdentity } from "@/providers/authProvider";

export interface TemplateRow extends Record<string, unknown> {
  id: string;
  category_name: string;
  position: number;
  text: string;
  sensitivity: string;
  phase: string;
  plan: string | null;
  active: boolean;
  deliveries_count: number;
}

const SENSITIVITY_OPTIONS = [
  { value: "normal", label: "Zwykłe" },
  { value: "sensitive", label: "Delikatne" },
];

const ACTIVE_OPTIONS = [
  { value: "true", label: "Włączone" },
  { value: "false", label: "Wyłączone" },
];

export function PromptTemplatesPage() {
  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<TemplateRow>({
      resource: "prompt-templates",
      sorters: { initial: [{ field: "category", order: "asc" }] },
    });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const { mutate: update, mutation } = useUpdate();
  const mozeEdytowac = (identity?.permissions ?? []).includes("prompts.write");

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Baza pytań"
        description="Pytania, które trafiają do narratorów. Treść i warianty gramatyczne pochodzą z repozytorium - tutaj włącza się je i wyłącza."
      />

      <FilterBar>
        <Select
          label="Wrażliwość"
          value={filterValue(filters, "sensitivity")}
          options={SENSITIVITY_OPTIONS}
          onChange={(value) => ustawFiltr("sensitivity", value)}
        />
        <Select
          label="Stan"
          value={filterValue(filters, "active")}
          options={ACTIVE_OPTIONS}
          onChange={(value) => ustawFiltr("active", value)}
        />
        <SearchInput
          label="Szukaj w treści"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Kategoria</Th>
                <Th>Pytanie</Th>
                <Th>Faza</Th>
                <Th>Wrażliwość</Th>
                <Th align="right">Wysyłki</Th>
                <Th align="right">Stan</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Żadne pytanie nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((template) => (
                <tr
                  key={template.id}
                  className={
                    template.active ? "transition-colors hover:bg-bg/60" : "opacity-60"
                  }
                >
                  <Td nowrap>{template.category_name}</Td>
                  <Td>{template.text}</Td>
                  <Td>{phaseLabel(template.phase)}</Td>
                  <Td>
                    <Badge tone={template.sensitivity === "sensitive" ? "warn" : "neutral"}>
                      {sensitivityLabel(template.sensitivity)}
                    </Badge>
                  </Td>
                  <Td align="right" numeric>
                    <span
                      title={`${template.deliveries_count} ${plural(
                        template.deliveries_count,
                        "wysyłka",
                        "wysyłki",
                        "wysyłek",
                      )}`}
                    >
                      {template.deliveries_count}
                    </span>
                  </Td>
                  <Td align="right">
                    {mozeEdytowac ? (
                      <SmallButton
                        disabled={mutation.isPending}
                        onClick={() =>
                          update({
                            resource: "prompt-templates",
                            id: template.id,
                            values: { active: !template.active },
                            successNotification: false,
                            errorNotification: false,
                          })
                        }
                      >
                        {template.active ? "Wyłącz" : "Włącz"}
                      </SmallButton>
                    ) : (
                      <Badge tone={template.active ? "ok" : "neutral"}>
                        {template.active ? "Włączone" : "Wyłączone"}
                      </Badge>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
